import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, CircularProgress, Typography, Alert } from '@mui/material'
import { useOktaAuth } from '../../contexts/OktaAuthContext'

const LoginCallback: React.FC = () => {
  const navigate = useNavigate()
  const { oktaAuth } = useOktaAuth()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const handleCallback = async () => {
      // Wait for oktaAuth to be initialized
      if (!oktaAuth) {
        console.log('Waiting for Okta Auth to initialize...')
        // Retry after a short delay
        setTimeout(() => {
          if (isMounted && !oktaAuth) {
            setError('Authentication service failed to initialize')
          }
        }, 3000)
        return
      }

      try {
        console.log('Handling login redirect...')

        // Handle the callback from Okta with timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Login callback timeout')), 10000)
        })

        await Promise.race([
          oktaAuth.handleLoginRedirect(),
          timeoutPromise
        ])

        if (isMounted) {
          console.log('Login redirect handled successfully')
          // Small delay to ensure auth state is updated
          setTimeout(() => {
            if (isMounted) {
              navigate('/', { replace: true })
            }
          }, 500)
        }
      } catch (err) {
        console.error('Login callback error:', err)

        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Authentication failed'

          // Provide more specific error messages
          if (errorMessage.includes('timeout')) {
            setError('Login process timed out. This may be due to network issues.')
          } else if (errorMessage.includes('CORS')) {
            setError('Network configuration error. Please contact your administrator.')
          } else if (errorMessage.includes('invalid_grant')) {
            setError('Invalid authentication response. Please try logging in again.')
          } else {
            setError(errorMessage)
          }

          // Redirect to home page after a delay to show the error
          setTimeout(() => {
            if (isMounted) {
              navigate('/', { replace: true })
            }
          }, 4000)
        }
      }
    }

    // Small delay to allow auth context to initialize
    const timer = setTimeout(handleCallback, 100)

    return () => {
      isMounted = false
      clearTimeout(timer)
    }
  }, [oktaAuth, navigate])

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ p: 3 }}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2" paragraph>
            {error}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            This error often resolves itself on the next login attempt. If it persists, please contact your administrator.
          </Typography>
        </Alert>
        <Typography variant="body2" color="textSecondary">
          Redirecting to home page in a few seconds...
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={2}
    >
      <CircularProgress size={60} />
      <Typography variant="h6" color="textSecondary">
        Completing sign in...
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Please wait while we redirect you to the application
      </Typography>
    </Box>
  )
}

export default LoginCallback
